/**
 * Shared paragraph-cloning helpers used by both `module-insert.ts` (CoC
 * single-section insert) and `qta-insert.ts` (Question To Answer multi-
 * paragraph insert).
 *
 * The big lesson from the CoC implementation: when we synthesise new
 * paragraphs into the docx editor doc, we MUST NOT copy the whole
 * `node.attrs` object from a donor paragraph — the docx-editor's
 * `ParagraphAttrs` carries identity (`paraId`, `textId`) and structural
 * (`_sectionProperties`, `sectionBreakType`, `pageBreakBefore`,
 * `pPrIns/Del`, list-numbering refs) fields that must remain unique per
 * paragraph. Cloning them silently corrupts the renderer's tracking and
 * the inserted block vanishes after a save/reload. `visualParaAttrs()`
 * allowlists only the *visual* attrs that are safe to share.
 */
import {
  Fragment,
  type Mark,
  type Node as PMNode,
  type Schema,
} from "prosemirror-model";

/** Marks that control the font family / size — the subset we override on a
 *  rich paste so the pasted content adopts the template font while keeping its
 *  own bold / italic / underline / colour etc. Names match the fork schema's
 *  mark specs (see docx-editor-core). */
const FONT_MARK_NAMES = new Set<string>(["fontFamily", "fontSize"]);

const SAFE_PARA_ATTR_KEYS = new Set<string>([
  "styleId",
  "_originalFormatting",
  "alignment",
  "outlineLevel",
  "spaceBefore",
  "spaceAfter",
  "lineSpacing",
  "lineSpacingRule",
  "spacingExplicit",
  "indentLeft",
  "indentRight",
  "indentFirstLine",
  "hangingIndent",
  "keepNext",
  "keepLines",
  "contextualSpacing",
  "borders",
  "shading",
  "tabs",
  "bidi",
  "defaultTextFormatting",
]);

/** Visual-only subset of a paragraph node's attrs — safe to apply to a
 *  freshly synthesised paragraph without breaking renderer identity or
 *  duplicating structural markers. Returns `null` if the donor is null. */
export const visualParaAttrs = (
  node: PMNode | null
): Record<string, unknown> | null => {
  if (!node) return null;
  const out: Record<string, unknown> = {};
  for (const k of Object.keys(node.attrs)) {
    if (!SAFE_PARA_ATTR_KEYS.has(k)) continue;
    const v = (node.attrs as Record<string, unknown>)[k];
    if (v === undefined) continue;
    out[k] = v;
  }
  return out;
};

/** First-text-child marks from a paragraph node — used as a stand-in for
 *  "the style of this paragraph's run". Heading paragraphs in OOXML keep
 *  their visible font / size / bold on the inline runs, not on the
 *  paragraph node, so cloning these onto our new text gives the inserted
 *  heading the same visual weight as the donor. */
export const firstRunMarks = (node: PMNode | null): readonly Mark[] => {
  if (!node) return [];
  const first = node.firstChild;
  return first && first.isText ? first.marks : [];
};

/** Every text run in the paragraph has the bold mark applied. Requires ALL
 *  runs (not just one) so partial-bold paragraphs — e.g. a field label
 *  `**ID Sighted:** 123` — don't get treated as headings. Shared by
 *  `qta-insert.ts`'s `isHeadingPara` and `slots.ts`'s `isHeadingBlock`. */
export const allRunsBold = (node: PMNode): boolean => {
  let runCount = 0;
  let allBold = true;
  node.forEach((child) => {
    if (!child.isText) return;
    if (!child.text || !child.text.trim()) return;
    runCount++;
    const hasBold = child.marks.some(
      (m) => m.type.name === "strong" || m.type.name === "bold"
    );
    if (!hasBold) allBold = false;
  });
  return runCount > 0 && allBold;
};

const isAllCapsText = (text: string): boolean => {
  const letters = text.replace(/[^a-zA-Z]/g, "");
  if (letters.length < 3) return false;
  return letters === letters.toUpperCase();
};

/** Visual fallback for detecting a heading paragraph when no Word
 *  `Heading{N}`/`Title` styleId or `outlineLevel` is present — the ~82% of
 *  real medico-legal templates that inline-format their section headings as
 *  plain "Normal text" paragraphs (ALLCAPS + manually bolded) rather than
 *  applying a real Word heading style. Mirrors the backend's
 *  `template_outline._classify` ALLCAPS+bold rule.
 *
 *  BOLD mixed-case is intentionally NOT a positive signal — profiling showed
 *  it's dominated by field labels (`ID Sighted:`, `Duration of
 *  Examination:`) and signature names, not section headings. */
export const looksLikeVisualHeading = (
  text: string,
  node: PMNode,
  maxLength = 80
): boolean => {
  if (text.length > maxLength) return false;
  if (!isAllCapsText(text)) return false;
  return allRunsBold(node);
};

/** The font-controlling subset (family / size) of a mark set — the marks we
 *  transplant onto pasted rich content so it matches the template font. */
export const fontMarksOf = (marks: readonly Mark[]): Mark[] =>
  marks.filter((m) => FONT_MARK_NAMES.has(m.type.name));

/** Return `fragment` with every text node's font marks replaced by
 *  `donorFontMarks`, preserving all non-font marks (bold / italic / underline
 *  / colour / links) and the node structure (paragraphs, lists, tables).
 *  Recurses into non-leaf nodes so nested content is remapped too. Used by
 *  pastePlugin to keep a rich paste's formatting while adopting the template's
 *  font. A no-op on fonts when `donorFontMarks` is empty. */
export const remapFontMarks = (
  fragment: Fragment,
  donorFontMarks: readonly Mark[]
): Fragment => {
  // Nothing to apply → leave the source untouched rather than stripping its
  // font to nothing (which would fall back to the schema default).
  if (donorFontMarks.length === 0) return fragment;
  // Only override the font axes the donor actually specifies: if the template
  // run carries a family but no explicit size, keep the source's own size
  // rather than stripping it to the schema default (and vice versa).
  const donorTypes = new Set(donorFontMarks.map((m) => m.type.name));
  const remap = (frag: Fragment): Fragment => {
    const out: PMNode[] = [];
    frag.forEach((child) => {
      if (child.isText) {
        let marks: readonly Mark[] = child.marks.filter(
          (m) => !donorTypes.has(m.type.name)
        );
        for (const donor of donorFontMarks) marks = donor.addToSet(marks);
        out.push(child.mark(marks));
      } else {
        out.push(child.copy(remap(child.content)));
      }
    });
    return Fragment.fromArray(out);
  };
  return remap(fragment);
};

/** Return `fragment` with every text node's font marks (family / size) removed,
 *  so the content falls back to the editor's per-document default font — the
 *  template's own theme font (`minorHAnsi`, Calibri on MEDirect templates),
 *  which body runs carry implicitly rather than as an explicit `fontFamily`
 *  mark. Preserves all non-font marks (bold / italic / underline / colour /
 *  links) and the node structure.
 *
 *  Used by pastePlugin for the rich-paste case where `donorMarksAt` finds no
 *  explicit font to transplant (the common one — a template whose body font is
 *  the theme default). Without stripping, the rich paste would fall through to
 *  the fork's native handler, which keeps the *source's* font (Times New Roman
 *  from the Code of Conduct panel) instead of matching the report. Stripping
 *  produces the same mark-less runs the plain-text paste path already relies on,
 *  which the fork renders in the document's resolved default font. */
export const stripFontMarks = (fragment: Fragment): Fragment => {
  const strip = (frag: Fragment): Fragment => {
    const out: PMNode[] = [];
    frag.forEach((child) => {
      if (child.isText) {
        out.push(
          child.mark(child.marks.filter((m) => !FONT_MARK_NAMES.has(m.type.name)))
        );
      } else {
        out.push(child.copy(strip(child.content)));
      }
    });
    return Fragment.fromArray(out);
  };
  return strip(fragment);
};

/** Colour-carrying marks. MDRS-403: a rich paste (e.g. selecting the coloured
 *  Code-of-Conduct panel) brings the source's text colour/highlight along; the
 *  report body is uncoloured, so we strip these on paste and let the runs inherit
 *  the document default. Names match the fork schema (`textColor`, `highlight`). */
const COLOR_MARK_NAMES = new Set<string>(["textColor", "highlight"]);
const BOLD_MARK_NAMES = new Set<string>(["bold", "strong"]);

const stripMarksBy = (
  fragment: Fragment,
  drop: (name: string) => boolean
): Fragment => {
  const walk = (frag: Fragment): Fragment => {
    const out: PMNode[] = [];
    frag.forEach((child) => {
      if (child.isText) {
        out.push(child.mark(child.marks.filter((m) => !drop(m.type.name))));
      } else {
        out.push(child.copy(walk(child.content)));
      }
    });
    return Fragment.fromArray(out);
  };
  return walk(fragment);
};

/** Return `fragment` with every text node's colour/highlight marks removed, so a
 *  rich paste adopts the report body colour. Preserves font / bold / italic /
 *  underline / links and node structure (MDRS-403). */
export const stripColorMarks = (fragment: Fragment): Fragment =>
  stripMarksBy(fragment, (name) => COLOR_MARK_NAMES.has(name));

/**
 * Normalise pasted top-level **paragraphs** to the destination's format
 * (MDRS-403). Each paragraph's attrs are *replaced* (not merged) with
 * `destParaAttrs` — the destination paragraph's visual attrs from
 * `visualParaAttrs` (spacing / alignment / `styleId` / …). Because it's a full
 * replacement, the SOURCE paragraph's own heading style (its `styleId` /
 * `outlineLevel`) is discarded — the paste adopts the *destination's* style and
 * spacing instead, so it lands as body text rather than a copied heading.
 *
 * The report's paragraph spacing transfers via whatever the destination carries:
 * an explicit `spaceBefore`/`spaceAfter`/`lineSpacing` attr, or its `styleId`
 * (all allowlisted in `SAFE_PARA_ATTR_KEYS`, so the fork resolves the style's
 * spacing). Edge case (MDRS-403, flagged for QA): if the destination paragraph
 * exposes NEITHER explicit spacing NOR a `styleId`, there's nothing to inherit
 * and the paste can still run together — real docx paragraphs almost always
 * carry a `styleId` (defaulting to "Normal"), so this is rare in practice.
 *
 * When a pasted paragraph is itself heading-shaped (ALLCAPS + every run bold —
 * a copied section title), its bold is stripped too so it lands as body text.
 * Genuine inline bold/italic in ordinary paragraphs is left intact, and
 * non-paragraph blocks (lists, tables) pass through untouched.
 */
export const normalizePastedParagraphs = (
  fragment: Fragment,
  destParaAttrs: Record<string, unknown> | null
): Fragment => {
  const out: PMNode[] = [];
  fragment.forEach((child) => {
    if (child.type.name !== "paragraph") {
      out.push(child);
      return;
    }
    let content = child.content;
    if (looksLikeVisualHeading(child.textContent.trim(), child)) {
      content = stripMarksBy(content, (name) => BOLD_MARK_NAMES.has(name));
    }
    // Replace (not merge) the paragraph attrs with the destination's visual set,
    // so any source heading style is dropped and the report's spacing applies.
    out.push(child.type.create(destParaAttrs ?? child.attrs, content));
  });
  return Fragment.fromArray(out);
};

/** Build an inline `text` fragment for a plain-text value. `\n` → a
 *  `hardBreak` node (or a space if the schema lacks one). When `marks` is
 *  provided, all emitted text nodes carry those marks. */
export const buildInlineNodes = (
  schema: Schema,
  value: string,
  marks: readonly Mark[] = []
): PMNode[] => {
  const hardBreakType = schema.nodes.hardBreak;
  const lines = value.split("\n");
  const out: PMNode[] = [];
  lines.forEach((line, idx) => {
    if (line) out.push(schema.text(line, marks.length ? marks : undefined));
    if (idx < lines.length - 1 && hardBreakType) {
      out.push(hardBreakType.create());
    } else if (idx < lines.length - 1) {
      out.push(schema.text(" "));
    }
  });
  return out;
};

/** Split a plain-text value into paragraph block nodes — one paragraph per
 *  newline-separated block — so a multi-paragraph refine keeps its paragraph
 *  structure instead of collapsing into one (the failure `buildInlineNodes`
 *  produced when replacing a cross-paragraph range with inline content).
 *
 *  Splits on blank-line boundaries first (`\n{2,}`); if the value uses single
 *  newlines between paragraphs (the shape a refine round-trip often returns),
 *  it falls back to splitting on every `\n`. Each resulting block becomes one
 *  paragraph carrying `marks` on its runs and the shared visual `paraAttrs`
 *  (never identity/structural attrs — see `visualParaAttrs`). Returns a
 *  single-paragraph result when the value has no usable break, and `[]` when
 *  the schema exposes no paragraph node. */
export const buildBlockNodes = (
  schema: Schema,
  value: string,
  marks: readonly Mark[] = [],
  paraAttrs: Record<string, unknown> | null = null
): PMNode[] => {
  const paragraphType = schema.nodes.paragraph ?? schema.nodes.doc_paragraph;
  if (!paragraphType) return [];
  const clean = (b: string) => b.replace(/^\n+/, "").replace(/\n+$/, "");
  const nonEmpty = (b: string) => b.trim().length > 0;
  let blocks = value
    .split(/\n{2,}/)
    .map(clean)
    .filter(nonEmpty);
  if (blocks.length <= 1) {
    const single = value.split(/\n/).map(clean).filter(nonEmpty);
    if (single.length > blocks.length) blocks = single;
  }
  const source = blocks.length ? blocks : [value];
  return source.map((block) =>
    paragraphType.create(paraAttrs ?? null, buildInlineNodes(schema, block, marks))
  );
};
